import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { openReportInNewTab } from '@ltvco/refresh-lib/hooks';
import { Stack } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  type ContactCompany,
  type ContactDetail,
  NoContactResults,
  reportGetters,
  type SerializedContactTeaserData,
  useReportRedirect,
} from '@ltvco/refresh-lib/v1';
import { ProfessionalCard } from '@ltvco/refresh-lib/v2';
import React, { useContext } from 'react';

import { constants } from 'appConstants';
import { useQueryParams } from 'utils/useQueryParams';

import { useSearchPersonResultsContext } from '../context';

import { MasonryCardsContainer } from './MasonryCardsContainer';
import { MobileProfessionalCard as MobileProfessionalCardVariation1 } from './NewCardsDesignVariation1/ProfessionalCards/MobileProfessionalCard';

interface ContactCardProps {
  searchContactResult: SerializedContactTeaserData;
  fullName?: string;
  company?: string;
}

export const ContactCards = ({
  searchContactResult,
  fullName,
  company,
}: ContactCardProps) => {
  const { contacts = [] } = searchContactResult;
  const { routingUtils } = useContext(AppConfig);
  const { redirectToSearchContactUrl, redirectToSearchContactUrlNewTab } =
    useReportRedirect();
  const { isMobile } = useScreenSize();
  const queryParams = useQueryParams();

  const { meetMobileCardInDesktopCriteria, isChatPage, handleContactReport } =
    useSearchPersonResultsContext();
  const getCompanyUrl = (company: string) =>
    routingUtils.searchContactUrl({ company });

  const handleCompanyClick = (
    company: string | ContactCompany,
    openInNewTab?: boolean
  ) => {
    const companyName = typeof company === 'string' ? company : company.name;

    if (openInNewTab) {
      redirectToSearchContactUrlNewTab({ company: companyName });
    } else {
      redirectToSearchContactUrl({ company: companyName });
    }
  };

  const handleViewReport = async (contactId: string) => {
    if (isChatPage) {
      queryParams.set('contact_id', contactId);
      const response = await reportGetters.contact({ queryParams });
      const permalink = response?.report?.permalink;

      if (permalink) {
        handleContactReport?.(permalink);
        return;
      }
    }

    openReportInNewTab(
      {
        contact_id: contactId,
        searchType: 'contact',
      },
      constants.links.baseUrl
    );
  };

  const Container = meetMobileCardInDesktopCriteria
    ? MasonryCardsContainer
    : Stack;

  if (!contacts) {
    return <NoContactResults />;
  }

  return (
    <Container spacing={meetMobileCardInDesktopCriteria ? 0 : 2}>
      {contacts.map((result: ContactDetail) => {
        if (isMobile || meetMobileCardInDesktopCriteria) {
          return (
            <React.Fragment key={result.id}>
              <MobileProfessionalCardVariation1
                {...result}
                onCompanyClick={(company) => handleCompanyClick(company, true)}
                onViewReport={handleViewReport}
              />
            </React.Fragment>
          );
        }

        return (
          <ProfessionalCard
            key={result.id}
            {...result}
            nameMatch={
              result.name.toLowerCase() === (fullName || '').toLowerCase()
            }
            companyToMatch={company || ''}
            onCompanyClick={(company) => handleCompanyClick(company, true)}
            getCompanyUrl={getCompanyUrl}
            onViewReport={handleViewReport}
            subsection=""
          />
        );
      })}
    </Container>
  );
};
